import JSTickFlashRenderer from './jsTickFlashRenderer';
import { getAriaText } from '@utils/helpers';

export default class JSQuoteRenderer extends JSTickFlashRenderer {
  accountId = '';
  ric;
  ticketType = '';
  action;
  cellClickListener = this.cellClickHandler.bind(this);
  keydownListener = this.keydownHandler.bind(this);

  // Important! During the initial rendering refresh method is called before the init.
  init(params) {
    super.init(params);

    if (!params.node.group) {
      this.initFields();

      if (this.getDisplayValue(params) === '-' || params.data.strategy === 'MLO') {
        this.eCurrent.setAttribute('aria-hidden', 'true');
      } else {
        let srElement = document.createElement('span');
        srElement.classList.add('sr-only');
        srElement.innerText = this.params.context.$t('white-label.globalLabels.buttonAria');
        this.eGui.appendChild(srElement);
      }

      this.eGui.addEventListener('click', this.cellClickListener);
      params.eGridCell.addEventListener('keydown', this.keydownListener);
    } else {
      this.eGui.setAttribute('aria-hidden', 'true');
    }
  }

  initFields() {
    if (this.params.context && this.params.context.wid && this.params.context.wid.startsWith('holdings')) {
      this.accountId = this.params.context.selectedAccountId;
    }

    const type = this.params.data['q._TYPE'];
    if (type === 'OPT') this.ticketType = 'option';
    else if (type === 'STO') this.ticketType = 'stock';
    else if (type === 'IND') this.ticketType = 'index';

    this.ric =
      this.params.data.ric ||
      this.params.data['q.RIC'] ||
      this.params.data.underlyingRic ||
      this.params.node.id.split('/').pop();

    this.quantity = this.params.data.quantity;
  }

  flashColor(newValue, oldValue) {
    if (this.params.data.strategy !== 'MLO') {
      super.flashColor(newValue, oldValue);
    }
  }

  refresh(params) {
    if (!params.node.group && !(this.getDisplayValue(params) === '-' || params?.data?.strategy === 'MLO')) {
      this.action = params.colDef.field.includes('BID') ? 'bid' : 'ask';
      this.eGui.classList.add(this.isBuy() ? 'buy' : 'sell');
    } else {
      this.eGui.classList.remove('buy', 'sell');
    }

    return super.refresh(params);
  }

  destroy() {
    this.params.eGridCell.removeEventListener('keydown', this.keydownListener);
    this.eGui.removeEventListener('click', this.cellClickListener);
    super.destroy();
  }

  getDisplayValue(params) {
    return Array.isArray(params.valueFormatted) ? params.valueFormatted[params.data.index] : params.valueFormatted;
  }

  isBuy() {
    let buySellAtType;
    if (this.ticketType === 'option') {
      buySellAtType = this.params.state.applicationSettings.orders.settings.options.buySellAtType;
    } else {
      buySellAtType = this.params.state.applicationSettings.orders.settings.stocks.buySellAtType;
    }
    if (
      (buySellAtType === 'buyAtAsk' && this.action === 'ask') ||
      (buySellAtType === 'buyAtBid' && this.action === 'bid')
    ) {
      return true;
    } else if (
      (buySellAtType === 'buyAtAsk' && this.action === 'bid') ||
      (buySellAtType === 'buyAtBid' && this.action === 'ask')
    ) {
      return false;
    }
  }

  isSell() {
    return !this.isBuy();
  }

  isShortPosition() {
    return this.quantity < 0;
  }

  isClose() {
    return this.accountId && ((this.isSell() && !this.isShortPosition()) || (this.isBuy() && this.isShortPosition()));
  }

  isFractionalTradingAllowed() {
    return this.params.context.$store.getters['accounts/isFractionalTradingAllowed'];
  }

  cellClickHandler() {
    if (!(this.getDisplayValue(this.params) === '-' || this.params?.data?.strategy === 'MLO')) {
      let modifier = '';
      let limit = '';
      if (this.action === 'bid' || this.action === 'ask') {
        modifier = 'limit';
        limit = Number.parseFloat(this.params.value);
      }

      let isFractional = !Number.isInteger(this.quantity) && !this.isBuy();
      let isFromOrderStatusBidAsk = this.params.data.orderKey && ['bid', 'ask'].includes(this.action);
      if (this.quantity === undefined || isFromOrderStatusBidAsk) {
        isFractional = false;
      }
      if (!this.isFractionalTradingAllowed) {
        this.quantity = Number.parseInt(this.quantity);
      }

      this.params.context.openOrderEntry({
        orderMode: { action: this.isBuy() ? 'BUY' : 'SELL', modifier: modifier, limit: limit, close: this.isClose() },
        legs: {
          firstOrder: {
            symbol: { ric: this.ric },
            ticketType: isFractional ? 'fractional' : this.ticketType,
            quantity: this.quantity,
            fractionalQuantityType: isFractional ? '#' : '',
            isFractional: isFractional
          }
        },
        accountId: this.accountId
      });
    }
  }

  keydownHandler(e) {
    if (e.code === 'Enter' || e.code === 'NumpadEnter' || e.code === 'Space') {
      this.cellClickHandler();
    }
  }

  getAriaText() {
    if (
      this.params.data?.allTickerArray &&
      !this.params.node.group &&
      (this.getDisplayValue(this.params) === '-' || this.params.data.strategy === 'MLO')
    ) {
      const tickerPart = this.params.data.allTickerArray.join(', ');
      if (Array.isArray(this.params.valueFormatted)) {
        return tickerPart + ' , ' + this.params.valueFormatted.join(' ');
      } else {
        return tickerPart + ' , ' + this.params.valueFormatted;
      }
    } else {
      return getAriaText(this.params);
    }
  }
}
